import { queryClient } from '../api/queryClient';
import { useAppStore } from '../store/appStore';
import { useAgentChatStore } from '../store/agentChatStore';
import { useTableViewStore } from '../store/tableViewStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useBoardShellStore } from '../store/boardShellStore';
import { useWorkspaceShellStore } from '../store/workspaceShellStore';
import { useCellEditStore } from '../store/cellEditStore';
import { supportSession } from './supportSession';

/**
 * Drop every piece of client-side state scoped to the signed-in identity so a
 * new session can't render the previous account's data (SC-2461).
 *
 * AuthProvider calls this on every identity transition — logout, the
 * `auth:lost` broadcast, and the start of login/register. The React Query
 * cache is a tab-lifetime singleton and both `byCatalog` stores live in module
 * memory, so without this they survive logout→login and the next account sees
 * the previous account's cached catalog (filters, chat, table pages) until a
 * manual hard refresh — the reported bug.
 *
 * Centralizing the list here means a new session-scoped store gets cleared by
 * adding one line, and any future auth-transition path only has to call this —
 * it never has to re-enumerate what "session state" means.
 *
 * NOT cleared: the device-level preferences in `appStore` (theme, dock width,
 * sidebar / facet-rail collapse). Those belong to the machine, not the account,
 * so wiping them on every logout would be a UX regression. Only the
 * account-scoped `activeCatalogId` is reset.
 */
export function clearSessionState({ keepSupportSession = false } = {}): void {
  queryClient.clear();
  useTableViewStore.getState().reset();
  useWorkspaceStore.getState().reset();
  useBoardShellStore.getState().reset();
  useWorkspaceShellStore.getState().reset();
  useAgentChatStore.getState().reset();
  useCellEditStore.getState().reset();
  useAppStore.getState().setActiveCatalogId(null);
  // The staff support-session marker (SC-2948) belongs to the session it was
  // entered from; a switch writes a fresh one AFTER calling this. Kept only when
  // another tab just wrote it for the session this tab is adopting.
  if (!keepSupportSession) supportSession.clear();
}
